import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { OrderState, OrderStore } from '@modules/order/state/order-store';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { SendToOption } from '@shared/models/send-to-option';
import { IdName } from '@shared/models/id-name';
import { ProcedureMap } from '@shared/models/procedure-map';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { LinkedCompany } from '@shared/models/linked-company';
import type { Maybe } from '@/types/globals';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderModel } from '@shared/models/rx-models/interfaces/order-model';
import { LoggerService } from '@core/services/logger/logger.service';

@Injectable({
	providedIn: 'root'
})
export class ProcedureLoadRxService {
	constructor(
		private readonly store: OrderStore,
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery,
		private readonly logger: LoggerService,
		private readonly procedureConfigurationService: ProcedureConfigurationService
	) {}

	readonly name = 'ProcedureLoadRxService';

	loadProcedureFlowOrder(): Observable<RxModel> {
		return combineLatest({
			rx: this.shellQuery.rx$,
			rxConfiguration: this.shellQuery.rxConfiguration$,
			companies: this.shellQuery.companies$
		}).pipe(
			filter(
				(latest): latest is { rx: RxModel; rxConfiguration: RxConfiguration; companies: LinkedCompany[] } =>
					!!latest.rx && !!latest.rxConfiguration && !!latest.companies && this.isAvailableProcedureMap(latest.rx)
			),
			map(({ rx, rxConfiguration, companies: labs }) => {
				const procedureMap = rxConfiguration.RxRules.ProceduresMap.find(x => x.Id === rx.Order.ProcedureMapId);
				const sendTo = this.getSelectedSendTo(procedureMap, labs, rx.Order);
				const dueDate = rx.Order.DueDate ? new Date(rx.Order.DueDate) : null;
				const treatmentStage = rxConfiguration.TreatmentStages.find(ts => ts.Id === rx.TreatmentStage) ?? null;

				const state: Partial<OrderState> = {
					isMultiBiteSelected: rx.MultiBiteScan,
					treatmentStage,
					dueDate,
					currentAlignerId: rx.AlignerNumber != null ? String(rx.AlignerNumber) : null,
					sendTo,
					procedureMap
				};

				this.updateOrder(state);

				return rx;
			}),
			catchError(err => {
				this.logger.error('Error loading procedure flow order', {
					module: this.name,
					error: err
				});
				throw err;
			})
		);
	}

	pullOrderSectionValues(rx: RxModel) {
		return combineLatest([
			this.orderQuery.procedure$,
			this.orderQuery.type$,
			this.orderQuery.sendTo$,
			this.orderQuery.treatmentStage$,
			this.orderQuery.dueDate$
		]).pipe(
			map(([procedure, type, sendTo, treatmentStage, dueDate]) => {
				const currentAlignerId = rx.AlignerNumber != null ? String(rx.AlignerNumber) : null;

				this.logger.info('Current order section values', {
					module: this.name,
					extendedParameters: {
						procedure: procedure?.Name ?? 'null',
						type: type?.Name ?? 'null',
						sendTo: sendTo?.Name ?? 'null',
						treatmentStage: treatmentStage?.Name ?? 'null',
						dueDate: dueDate ? dueDate.toISOString() : 'null',
						currentAlignerId: currentAlignerId ?? 'null'
					}
				});

				return {
					procedure,
					type,
					sendTo,
					treatmentStage,
					dueDate,
					currentAlignerId
				};
			})
		);
	}

	private isAvailableProcedureMap(rx: RxModel): boolean {
		const id = rx.Order?.ProcedureMapId;
		const isAvailable = id === null || id === undefined || this.orderQuery.availableProcedureMaps.some(({ Id }) => Id === id);

		this.logger.info('Checking procedure availability', {
			module: this.name,
			extendedParameters: {
				procedureMapId: String(id),
				isAvailable: String(isAvailable)
			}
		});

		return isAvailable;
	}

	private getSelectedSendTo(procedureMap: Maybe<ProcedureMap>, labs: IdName[], order: OrderModel): SendToOption | null {
		const lab = labs.find(l => l.Id === order?.ShipToId);

		if (lab) {
			return { ...lab, SendToTypes: procedureMap?.SendToTypes };
		}

		return (
			this.procedureConfigurationService.getSendToIdForNotLabs(procedureMap?.SendToTypes) ??
			// @ts-expect-error - TODO: remove this comment and fix type error
			this.getSendToOptionFromOrder(order, procedureMap?.SendToTypes) ??
			// @ts-expect-error - TODO: remove this comment and fix type error
			this.procedureConfigurationService.getSendToForUnknownProcedureMaps([procedureMap])?.[0] ??
			null
		);
	}

	private getSendToOptionFromOrder(order: OrderModel, sendToType: number): SendToOption | null {
		// This fallback should never be used unless RX is read-only.
		if (!this.shellQuery.isReadOnly || !order?.ShipToId || !order?.ShipToName) {
			return null;
		}

		// If the backend gave us an existing lab/company id, but it wasn't part of labs list,
		// keep it so the form can show something instead of falling back to unknown/null.
		// This option must NOT look like a "not-lab" option (those are handled earlier).
		return {
			Id: order.ShipToId,
			Name: order.ShipToName,
			SendToTypes: sendToType
		};
	}

	private updateOrder(state: Partial<OrderState>) {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const { Id, Comment, ProcedureId, TypeId, SendToTypes } = state.procedureMap ?? {};

		this.logger.info('Updating store with order details', {
			module: this.name,
			extendedParameters: {
				procedureMap: JSON.stringify({ Id, Comment, ProcedureId, TypeId, SendToTypes }),
				sendTo: JSON.stringify(state.sendTo)
			}
		});

		this.store.update({ ...state });
	}
}
